import axiosInstance from "../../utils/axiosConfig";
import React, { useEffect, useState } from "react";
import { TailSpin } from "react-loader-spinner";

import { noTransaction } from "../../assets/images";
import { API_URL } from "../../constant";
import { useAuth } from "../../context/auth-context";
import CreateTask from "../dashboard/CreateTask";
import OngoingTasks from "../tasks/OngoingTasks";
import PastTasks from "../tasks/PastTasks";

/**
 * Task applications UI (ongoing + approved lists, approve action, create-task modal)
 * previously shown on the standalone tasks page — now embedded under Rewards.
 */
const TaskApplicationsPanel: React.FC = () => {
  const { query, isAuthenticated, logout } = useAuth();
  const [openCreateTaskModal, setOpenCreateTaskModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tasks, setTasks] = useState<any[]>([]);
  const [pastTasks, setPastTasks] = useState<any[]>([]);
  const [refetchCount, setRefetchCount] = useState(0);
  const [taskScreen, setTaskScreen] = useState(1);

  useEffect(() => {
    setLoading(true);
    axiosInstance
      .get(`${API_URL}api/tasks/applications`)
      .then((r: any) => {
        const applications = r.data?.data?.applications ?? [];
        const filtered = applications.filter(
          (item: any) => item.status === "pending"
        );
        const filteredPast = applications.filter(
          (item: any) => item.status === "approved"
        );
        setTasks(filtered);
        setPastTasks(filteredPast);
        setLoading(false);
      })
      .catch((e: any) => {
        console.log(e.response?.data?.message);
        if (
          e.response?.status === 401 &&
          e.response?.data?.message === "Inavlid token. Please login again!"
        ) {
          logout();
        }
        setLoading(false);
      });
  }, [isAuthenticated, refetchCount, logout]);

  return (
    <div className="w-full">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between lgss:gap-0">
        <div className="hidden items-center gap-6 rounded-lg bg-transparent px-2 lgss:flex">
          <button
            type="button"
            onClick={() => setTaskScreen(1)}
            className={
              taskScreen === 1
                ? "text-[16px] font-bold text-white"
                : "text-[15px] font-bold text-[#4E606E]"
            }
          >
            Ongoing ({tasks.length})
          </button>
          <button
            type="button"
            onClick={() => setTaskScreen(2)}
            className={
              taskScreen === 2
                ? "text-[16px] font-bold text-white"
                : "text-[15px] font-bold text-[#333F48]"
            }
          >
            Approved ({pastTasks.length})
          </button>
        </div>
        <div className="flex w-full flex-wrap items-center justify-between gap-3 sm:justify-end lgss:w-auto">
          <div className="flex gap-2 rounded-lg border border-[#2C363E] bg-[#161E25]/80 p-1 lgss:hidden">
            <button
              type="button"
              onClick={() => setTaskScreen(1)}
              className={`rounded-md px-3 py-1.5 text-[13px] font-semibold ${
                taskScreen === 1
                  ? "bg-secondary_button text-white"
                  : "text-[#869AA9]"
              }`}
            >
              Ongoing ({tasks.length})
            </button>
            <button
              type="button"
              onClick={() => setTaskScreen(2)}
              className={`rounded-md px-3 py-1.5 text-[13px] font-semibold ${
                taskScreen === 2
                  ? "bg-secondary_button text-white"
                  : "text-[#869AA9]"
              }`}
            >
              Approved ({pastTasks.length})
            </button>
          </div>
          <button
            type="button"
            onClick={() => setOpenCreateTaskModal((prev) => !prev)}
            className="rounded-xl bg-secondary_button px-4 py-2 text-[15px] font-medium text-white"
          >
            Create task
          </button>
        </div>
      </div>

      {taskScreen === 1 ? (
        <div className="mt-6 h-[min(552px,62vh)] w-full overflow-auto rounded-tl-lg bg-[#0D0D0D] scrollbar-thin scrollbar-track-white scrollbar-thumb-[#60B7F6]">
          <table className="w-full">
            <thead>
              <tr className="flex h-[56px] items-center justify-start rounded-t-lg bg-[#1C2227] px-6 sm:px-10 lgss:px-16">
                <th className="mr-4 w-1/5 overflow-hidden whitespace-nowrap text-left text-[15px] text-[#617889]">
                  Name
                </th>
                <th className="mr-4 w-1/5 overflow-hidden whitespace-nowrap text-left text-[15px] text-[#617889]">
                  Email
                </th>
                <th className="mr-4 w-2/5 overflow-hidden whitespace-nowrap text-left text-[15px] text-[#617889]">
                  Task
                </th>
                <th className="w-1/5 overflow-hidden whitespace-nowrap text-left text-[15px] text-[#617889]">
                  Points
                </th>
                <th className="w-1/5 overflow-hidden whitespace-nowrap text-center text-[15px] text-[#617889]">
                  Action
                </th>
              </tr>
            </thead>
            {loading ? (
              <div className="mt-12 flex w-full justify-center">
                <TailSpin color="#0B75C2" height={70} width={90} />
              </div>
            ) : (
              <div className="w-full">
                {tasks.length >= 1 ? (
                  <table className="w-full">
                    {tasks
                      .filter((searchValue) => {
                        return query.toLowerCase() === ""
                          ? searchValue
                          : searchValue.task.name
                              .toLowerCase()
                              .includes(query) ||
                              searchValue.user.profile.firstName
                                .toLowerCase()
                                .includes(query) ||
                              searchValue.user.profile.lastName
                                .toLowerCase()
                                .includes(query);
                      })
                      .map((task, index) => (
                        <OngoingTasks
                          task={task}
                          key={task._id ?? index}
                          setRefetchCount={setRefetchCount}
                        />
                      ))}
                  </table>
                ) : (
                  <div className="mt-12 flex w-full flex-col items-center justify-center">
                    <img src={noTransaction} alt="" />
                    <h4 className="pt-2 font-bold text-[#869AA9]">
                      No applied task at the moment
                    </h4>
                    <p className="pt-2 text-[12px] text-[#869AA9]">
                      All applied tasks will show up here
                    </p>
                  </div>
                )}
              </div>
            )}
          </table>
        </div>
      ) : (
        <div className="mt-6">
          <PastTasks pastTasks={pastTasks} query={query} />
        </div>
      )}

      {openCreateTaskModal ? (
        <CreateTask setOpenCreateTaskModal={setOpenCreateTaskModal} />
      ) : null}
    </div>
  );
};

export default TaskApplicationsPanel;
